<script lang="ts">
  import { landingPath, type Resolution } from "$lib/auth/guards";
  import { preferredPortalOrganization } from "$lib/domain/org";
  import { router, link } from "../../router.svelte";
  import { session } from "$lib/stores/session.svelte";
  import { prefs } from "$lib/stores/prefs.svelte";
  import AppLogo from "$lib/components/AppLogo.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Drawer from "$lib/components/Drawer.svelte";
  import AccountMenu from "$lib/components/AccountMenu.svelte";
  import type { MenuItem } from "$lib/components/types";
  import AdminCapabilityDenied from "./AdminCapabilityDenied.svelte";
  import { isAdminPage, loadAdminPage, loadedAdminPage, preloadAdminPage, type AdminPageName } from "./admin-pages";
  import { queryClient } from "$lib/query/cache.svelte";
  import { invalidateCustomerResources } from "./customer-queries";

  interface Props {
    view: Extract<Resolution, { kind: "admin" | "admin-denied" }>;
  }
  const { view }: Props = $props();

  // The customer list, every customer workspace, and the quote composer all live
  // under the same nav item.
  const entitlementsActive = $derived(
    view.name === "admin.entitlements" || view.name === "admin.customer" || view.name === "admin.customer.quote.new"
  );
  const customerId = $derived(
    view.name === "admin.customer" || view.name === "admin.customer.quote.new"
      ? router.match.params.customerId ?? ""
      : ""
  );
  // Organization recovery is a distinct support capability; the nav item only exists
  // for operators who actually hold it (the route itself 404s without it).
  const organizationsActive = $derived(view.name === "admin.organizations");
  // Contract library (templates) and the signing queue are separate nav items, each
  // owning its own detail pages. Keyed by id so switching records remounts the page.
  const contractsActive = $derived(
    view.name === "admin.contracts" || view.name === "admin.contract" || view.name === "admin.contract.new"
  );
  const contractQueueActive = $derived(view.name === "admin.contract-queue" || view.name === "admin.contract-queue.item");
  const contractId = $derived(
    view.name === "admin.contract" || view.name === "admin.contract-queue.item"
      ? router.match.params.contractId ?? ""
      : ""
  );
  const revenueActive = $derived(view.name === "admin.revenue");
  const auditActive = $derived(view.name === "admin.audit");
  // Discord operations exist only where the server grants `discord.manage`; the nav
  // item follows the capability the same way Organizations does.
  const discordActive = $derived(view.name === "admin.discord");

  const organizationView = $derived(
    preferredPortalOrganization(session.organizations, prefs.lastOrgId)
  );
  const accountMenuItems = $derived<MenuItem[]>(
    organizationView
      ? [{
          id: "organization-view",
          label: "Switch to customer portal",
          description: organizationView.name,
          onSelect: () => router.navigate(landingPath(organizationView, organizationView.id))
        }]
      : []
  );
  const initials = $derived(
    (session.user?.displayName ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("")
  );

  $effect(() => {
    const path = view.name === "admin.organizations"
      ? "/admin/organizations"
      : view.name === "admin.revenue"
        ? "/admin/revenue"
        : view.name === "admin.audit"
          ? "/admin/audit"
          : view.name === "admin.discord"
            ? "/admin/discord"
            : contractsActive
              ? "/admin/contracts"
              : contractQueueActive ? "/admin/contract-queue" : "/admin/entitlements";
    if (prefs.lastAdminPath !== path) prefs.setLastAdminPath(path);
  });

  // ------------------------------------------------------------------ page loading
  // Pages come from one lazily loaded table. A chunk that has already arrived (the
  // nav link or a list row warmed it) renders in the same frame as the navigation, so
  // the page heading exists when App focuses `[data-page-title]` — no loading flash.
  const pageName = $derived<AdminPageName | null>(
    view.kind === "admin" && isAdminPage(view.name) ? view.name : null
  );
  let chunkArrivals = $state(0);
  let failedPage = $state<AdminPageName | null>(null);
  const PageComponent = $derived.by(() => {
    void chunkArrivals;
    return pageName ? loadedAdminPage(pageName) : undefined;
  });

  $effect(() => {
    const name = pageName;
    if (!name || PageComponent) return;
    let cancelled = false;
    failedPage = null;
    loadAdminPage(name).then(
      () => {
        if (!cancelled) chunkArrivals += 1;
      },
      () => {
        if (!cancelled) failedPage = name;
      }
    );
    return () => {
      cancelled = true;
    };
  });

  // Keyed so switching customers or contracts remounts the page: every per-record
  // fetch, dialog, and tab selection resets instead of leaking across, and a
  // half-composed quote never carries over onto a different customer.
  const pageKey = $derived(`${view.name}:${customerId}:${contractId}`);
  const pageProps = $derived<Record<string, unknown>>(
    view.name === "admin.customer" || view.name === "admin.customer.quote.new"
      ? { customerId }
      : view.name === "admin.contract" || view.name === "admin.contract-queue.item"
        ? { contractId }
        : view.name === "admin.contract.new"
          ? { contractId: null }
          : {}
  );

  // The composer creates quotes (and may hold a subscription pending modification)
  // without going through this cache, so coming back from it refreshes exactly those.
  let previousView: { name: string; customerId: string } | null = null;
  $effect(() => {
    const next = { name: view.name as string, customerId };
    if (previousView?.name === "admin.customer.quote.new" && previousView.customerId) {
      invalidateCustomerResources(previousView.customerId, ["quotes", "subscription"]);
    }
    previousView = next;
  });

  // Support data never outlives the console: leaving it (sign-out, switching to the
  // customer portal) drops every cached admin read.
  $effect(() => () => queryClient.remove(["admin"]));

  let navOpen = $state(false);

  function warm(name: AdminPageName): () => void {
    return () => preloadAdminPage(name);
  }
</script>

{#snippet navLink(href: string, label: string, active: boolean, page: AdminPageName, icon: string)}
  <li>
    <a
      {href}
      use:link={{ beforeNavigate: () => (navOpen = false) }}
      class="nav-link"
      class:active
      aria-current={active ? "page" : undefined}
      onpointerenter={warm(page)}
      onfocus={warm(page)}
    >
      {#if icon === "#"}
        <span class="nav-glyph" aria-hidden="true">#</span>
      {:else}
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d={icon} fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
      {/if}
      <span>{label}</span>
    </a>
  </li>
{/snippet}

{#snippet adminNav()}
  <nav aria-label="Admin navigation">
    <ul class="nav">
      {@render navLink("/admin/entitlements", "Customers", entitlementsActive, "admin.entitlements", "M4 6.5h12M4 10h12M4 13.5h8")}
      {#if session.canSupportOrganizations}
        {@render navLink("/admin/organizations", "Organizations", organizationsActive, "admin.organizations", "M3.5 16.5V6.2L9 3.5v13M9 8.3h6.2a1.3 1.3 0 0 1 1.3 1.3v6.9M6 8.4h.01M6 11.4h.01M12 11.4h.01M12 14h.01")}
      {/if}
      {#if session.canManageInvoices}
        {@render navLink("/admin/contracts", "Contracts", contractsActive, "admin.contracts", "M5.5 3.5h7l3 3v10h-10zM12.5 3.5v3h3M7.5 10h5M7.5 13h5")}
        {@render navLink("/admin/contract-queue", "Pending Contracts", contractQueueActive, "admin.contract-queue", "M4 5.5h12M4 10h8M4 14.5h5M14 12.5l1.5 1.5 3-3")}
      {/if}
      {@render navLink("/admin/revenue", "Revenue", revenueActive, "admin.revenue", "M3.5 15.5V10m4.3 5.5V6.5m4.3 9V9m4.4 6.5V3.5")}
      {@render navLink("/admin/audit", "Audit", auditActive, "admin.audit", "M5 3.5h10v13H5zM7.5 7h5M7.5 10h5M7.5 13h3")}
      {#if session.canManageDiscord}
        {@render navLink("/admin/discord", "Discord", discordActive, "admin.discord", "#")}
      {/if}
    </ul>
  </nav>
{/snippet}

<div class="shell">
  <aside class="sidebar">
    <a class="brand" href="/admin/entitlements" use:link>
      <AppLogo size={26} />
      <span>EpiDev Admin</span>
    </a>
    {@render adminNav()}
    <div class="account">
      <span class="avatar" aria-hidden="true">{initials}</span>
      <span class="who">
        <span class="who-name">{session.user?.displayName ?? "Signed in"}</span>
        {#if session.user?.email}<span class="who-email">{session.user.email}</span>{/if}
      </span>
      <AccountMenu items={accountMenuItems} />
    </div>
  </aside>

  <div class="main-col">
    <!-- Narrow viewports only: the sidebar carries brand and account on desktop. -->
    <header class="topbar">
      <button class="hamburger" aria-label="Open navigation" aria-expanded={navOpen} onclick={() => (navOpen = true)}>
        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
      </button>
      <div class="topbar-title">
        <AppLogo size={24} />
        <span class="wordmark">Administration</span>
      </div>
      <div class="topbar-right">
        <AccountMenu items={accountMenuItems} />
      </div>
    </header>

    <main id="main-content" class="content">
      {#if view.kind === "admin-denied"}
        <AdminCapabilityDenied capability={view.capability} />
      {:else if PageComponent}
        {#key pageKey}
          <PageComponent {...pageProps} />
        {/key}
      {:else if failedPage && failedPage === pageName}
        <div class="page-failed">
          <Banner tone="error" title="We couldn't load this page" role="alert">
            Refresh the page and try again.
          </Banner>
        </div>
      {:else}
        <div class="page-loading" role="status" aria-live="polite"><span class="sr-only">Loading page…</span></div>
      {/if}
    </main>
  </div>
</div>

{#if navOpen}
  <Drawer open={navOpen} title="Admin navigation" onclose={() => (navOpen = false)}>
    {@render adminNav()}
  </Drawer>
{/if}

<style>
  .shell {
    display: grid;
    grid-template-columns: var(--sidebar-w) 1fr;
    min-height: 100vh;
  }
  .sidebar {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-4) var(--space-3) var(--space-3);
    border-right: 1px solid var(--line-soft);
    background: var(--bg-2-veil);
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 8px;
    color: var(--heading);
    font-size: 14.5px;
    font-weight: 650;
  }
  .brand:hover {
    color: var(--heading);
  }
  .nav {
    display: flex;
    flex-direction: column;
    gap: 1px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .nav-link {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 34px;
    padding: 6px 10px;
    border-radius: var(--radius-chip);
    color: var(--text-soft);
    font-size: 13.5px;
    font-weight: 550;
    transition: color var(--dur-2) var(--ease), background var(--dur-2) var(--ease);
  }
  .nav-link:hover {
    background: var(--surface-raised);
    color: var(--heading);
  }
  .nav-link svg {
    flex: none;
    width: 17px;
    height: 17px;
    color: var(--muted);
  }
  .nav-glyph {
    display: grid;
    flex: none;
    place-items: center;
    width: 17px;
    height: 17px;
    color: var(--muted);
    font-family: var(--font-mono);
    font-size: 15px;
    font-weight: 700;
    line-height: 1;
  }
  .nav-link.active {
    background: var(--accent-hover);
    color: var(--heading);
  }
  .nav-link.active svg,
  .nav-link.active .nav-glyph {
    color: var(--accent);
  }
  .account {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: auto;
    padding: 10px 4px 2px 8px;
    border-top: 1px solid var(--line-soft);
  }
  .avatar {
    display: grid;
    flex: none;
    place-items: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--accent-edge);
    border-radius: 50%;
    background: var(--accent-fill);
    color: var(--heading);
    font-size: 11px;
    font-weight: 700;
  }
  .who {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    line-height: 1.3;
  }
  .who-name,
  .who-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .who-name {
    color: var(--heading);
    font-size: 13px;
    font-weight: 600;
  }
  .who-email {
    color: var(--muted);
    font-size: 12px;
  }
  .main-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .topbar {
    height: var(--topbar-h);
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 16px;
    border-bottom: 1px solid var(--line-soft);
    position: sticky;
    top: 0;
    /* Same site glass as the customer shell's topbar — see AppShell. */
    background: var(--glass);
    backdrop-filter: saturate(140%) blur(14px);
    -webkit-backdrop-filter: saturate(140%) blur(14px);
    z-index: 10;
  }
  .topbar-title,
  .topbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar-right {
    margin-left: auto;
  }
  .wordmark {
    color: var(--heading);
    font-weight: 600;
  }
  .hamburger {
    display: grid;
    width: 38px;
    height: 38px;
    place-items: center;
    border: 1px solid var(--line-soft);
    border-radius: 8px;
    background: transparent;
    color: var(--text);
    cursor: pointer;
  }
  .hamburger svg {
    width: 20px;
    height: 20px;
  }
  .content {
    width: 100%;
    /* The console's measure. Wider than the customer shell's --maxw-app because
       entitlement and quote tables are wide; centred so it does not drift left on a
       very large display. */
    max-width: var(--maxw-admin);
    margin: 0 auto;
    min-width: 0;
    flex: 1 0 auto;
    display: flex;
  }
  .page-loading {
    flex: 1 1 auto;
  }
  .page-failed {
    width: 100%;
    padding: var(--space-6) clamp(16px, 2.4vw, 32px);
  }
  /* Desktop: the sidebar is pinned and the content column scrolls on its own, so the
     nav stays put while a long customer history scrolls. No topbar — the sidebar
     already carries the brand and the account. */
  @media (min-width: 1081px) {
    .shell {
      height: 100vh;
      overflow: hidden;
    }
    .sidebar {
      position: sticky;
      top: 0;
      height: 100vh;
    }
    .main-col {
      height: 100%;
      min-height: 0;
      overflow-y: auto;
    }
    .topbar {
      display: none;
    }
  }
  @media (max-width: 1080px) {
    .shell {
      grid-template-columns: 1fr;
    }
    .sidebar {
      display: none;
    }
  }
  @media (max-width: 560px) {
    .wordmark {
      display: none;
    }
  }
</style>
